import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'

import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { formatMoney, formatPercentage } from '../../../packages/utils/numberFormatters'
import InfoBlock from '../../components/InfoBlock'
import { InvestorConvertible } from 'types/InvestorEntity'

const compareBySizeAndName = (a: InvestorConvertible, b: InvestorConvertible) => {
    const aSize = a.amountInCash || 0
    const bSize = b.amountInCash || 0
    if (aSize > bSize) {
        return -1
    }
    if (aSize < bSize) {
        return 1
    }
    if (a.name < b.name) {
        return -1
    }
    if (a.name > b.name) {
        return 1
    }
    return 0
}

function ConvertibleInvestors({ convertible }: { convertible: ConvertibleEntity }) {
    const { i18n, t } = useTranslation()
    const locale = i18n.language

    const { investors } = convertible
    let content = null

    if (investors.length > 0) {
        const totalInvestorsSize = investors.reduce((acc, row) => acc + (row.amountInCash || 0), 0) || null
        const totalInvestorsPercentage = investors.reduce((acc, row) => acc + (row.amountInPercentage || 0), 0) || null

        content = (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('site.table.name')}</TableCell>
                            <TableCell>
                                {t('site.table.amount')} ({convertible.currency})
                            </TableCell>
                            <TableCell>{t('pages.convertibles.investors.share')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {investors.sort(compareBySizeAndName).map((investor) => {
                            return (
                                <TableRow key={investor.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell>
                                        {investor.name === '?' ? (
                                            <Typography variant='body3'>{t('pages.convertibles.investors.unnamed')}</Typography>
                                        ) : (
                                            <Link to={`/investors/${investor.investorId}`}>{investor.name}</Link>
                                        )}
                                    </TableCell>
                                    <TableCell component='td' scope='row'>
                                        {investor.amountInCash !== null
                                            ? formatMoney(investor.amountInCash, {
                                                  locale,
                                                  decimals: 0,
                                              })
                                            : '*'}
                                    </TableCell>
                                    <TableCell component='td' scope='row'>
                                        {investor.amountInCash !== null
                                            ? formatPercentage(investor.amountInPercentage, {
                                                  locale,
                                                  decimals: 1,
                                              })
                                            : '*'}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        <TableRow>
                            <TableCell>
                                <b>{t('site.table.total')}</b>
                            </TableCell>
                            <TableCell>
                                <b>{formatMoney(totalInvestorsSize, { locale, decimals: 0 })}</b>
                            </TableCell>
                            <TableCell>
                                <b>
                                    {totalInvestorsPercentage
                                        ? formatPercentage(totalInvestorsPercentage, {
                                              locale,
                                              minDecimals: 0,
                                              maxDecimals: 1,
                                          })
                                        : ''}
                                </b>
                            </TableCell>
                        </TableRow>
                        {totalInvestorsPercentage === null ||
                            (totalInvestorsPercentage < 99 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <i>{t('pages.convertibles.investors.incompleteInfo')}</i>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    } else {
        content = (
            <span>
                <i>{t('pages.convertibles.investors.incompleteInfo')}</i>
            </span>
        )
    }

    return (
        <InfoBlock title={t('entities.investor.plural')} loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}

export default ConvertibleInvestors
